<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'
import { requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Product',
  },
})

const ability = useAbility()
const { loading, errors, request } = useApiRequest()

// -----------------------------------------------------------------------
// State
// -----------------------------------------------------------------------
const categories = ref([])
const search = ref('')
const activeOnly = ref(false)
const isFetching = ref(false)

const dialog = ref(false)
const dialogMode = ref('create') // 'create' | 'edit'
const dialogLoading = ref(false)
const deleteDialog = ref(false)
const selectedItem = ref(null)

const defaultForm = () => ({
  name: '',
  color: '#1976D2',
  icon: '',
  is_active: true,
  sort_order: 0,
})

const form = ref(defaultForm())
const formRef = ref()

const headers = [
  { title: '', key: 'color_badge', width: '8px', sortable: false },
  { title: 'Nama', key: 'name' },
  { title: 'Slug', key: 'slug' },
  { title: 'Status', key: 'is_active', width: '110px' },
  { title: 'Urutan', key: 'sort_order', width: '90px' },
  { title: 'Aksi', key: 'actions', sortable: false, width: '100px', align: 'end' },
]

const colorPresets = [
  '#E53935', '#D81B60', '#8E24AA', '#5E35B1',
  '#1E88E5', '#039BE5', '#00ACC1', '#00897B',
  '#43A047', '#7CB342', '#F4511E', '#FB8C00',
  '#546E7A', '#757575',
]

// -----------------------------------------------------------------------
// Fetch — ✅ FIX: response?.data ?? response
// -----------------------------------------------------------------------
const fetchCategories = async () => {
  isFetching.value = true
  try {
    const params = {}
    if (activeOnly.value) params.active_only = 1
    if (search.value)     params.search = search.value

    const response = await useApi('/api/v1/categories', { query: params }).get()
    const data = response?.data ?? response

    // Backend return: ApiResponse::success(collection) → data = array langsung
    categories.value = Array.isArray(data) ? data : (data?.data ?? [])
  } finally {
    isFetching.value = false
  }
}

// -----------------------------------------------------------------------
// Dialog helpers
// -----------------------------------------------------------------------
const openCreate = () => {
  dialogMode.value = 'create'
  form.value = defaultForm()
  formRef.value?.reset()
  dialog.value = true
}

const openEdit = (item) => {
  dialogMode.value = 'edit'
  selectedItem.value = item
  form.value = {
    name:       item.name,
    color:      item.color      ?? '#1976D2',
    icon:       item.icon       ?? '',
    is_active:  item.is_active,
    sort_order: item.sort_order ?? 0,
  }
  dialog.value = true
}

const confirmDelete = (item) => {
  selectedItem.value = item
  deleteDialog.value = true
}

// -----------------------------------------------------------------------
// CRUD — ✅ FIX: response parsing
// -----------------------------------------------------------------------
const submitForm = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid) return

  dialogLoading.value = true

  const res = await request(async () => {
    if (dialogMode.value === 'create') {
      return await useApi('/api/v1/categories').post({ body: form.value })
    } else {
      return await useApi(`/api/v1/categories/${selectedItem.value.id}`).put({ body: form.value })
    }
  })

  dialogLoading.value = false

  if (res) {
    dialog.value = false
    await fetchCategories()
  }
}

const deleteCategory = async () => {
  const res = await request(() =>
    useApi(`/api/v1/categories/${selectedItem.value.id}`).delete()
  )
  if (res !== null) {
    deleteDialog.value = false
    await fetchCategories()
  }
}

// -----------------------------------------------------------------------
// Lifecycle
// -----------------------------------------------------------------------
onMounted(fetchCategories)

let searchTimer = null
watch([activeOnly], fetchCategories)
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(fetchCategories, 400)
})
</script>

<template>
  <div>
    <!-- Header -->
    <VRow class="mb-2">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">Kategori</h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Kelompokkan produk berdasarkan kategori
        </p>
      </VCol>
    </VRow>

    <VCard>
      <!-- Toolbar -->
      <VCardText class="d-flex align-center gap-3 flex-wrap">
        <VTextField
          v-model="search"
          placeholder="Cari kategori..."
          prepend-inner-icon="ri-search-line"
          density="compact"
          hide-details
          clearable
          style="max-width: 280px"
        />
        <VCheckbox
          v-model="activeOnly"
          label="Aktif saja"
          density="compact"
          hide-details
          class="mt-0"
        />
        <VSpacer />
        <VBtn
          v-if="ability.can('create', 'Product')"
          color="primary"
          prepend-icon="ri-add-line"
          @click="openCreate"
        >
          Tambah Kategori
        </VBtn>
      </VCardText>

      <VDivider />

      <!-- Table -->
      <VDataTable
        :headers="headers"
        :items="categories"
        :loading="isFetching"
        hover
        no-data-text="Belum ada kategori"
      >
        <!-- Color badge -->
        <template #item.color_badge="{ item }">
          <div
            class="rounded"
            :style="{
              width: '8px',
              height: '36px',
              backgroundColor: item.color ?? '#9E9E9E',
            }"
          />
        </template>

        <!-- Name + icon -->
        <template #item.name="{ item }">
          <div class="d-flex align-center gap-2">
            <VIcon
              v-if="item.icon"
              :icon="item.icon"
              size="18"
              :color="item.color ?? 'primary'"
            />
            <span class="font-weight-medium">{{ item.name }}</span>
          </div>
        </template>

        <!-- Slug -->
        <template #item.slug="{ item }">
          <code class="text-caption text-medium-emphasis">{{ item.slug }}</code>
        </template>

        <!-- Status -->
        <template #item.is_active="{ item }">
          <VChip
            :color="item.is_active ? 'success' : 'default'"
            size="small"
            variant="tonal"
          >
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('update', 'Product')"
            icon size="small" variant="text" color="primary"
            @click="openEdit(item)"
          >
            <VIcon icon="ri-edit-line" />
            <VTooltip activator="parent">Edit</VTooltip>
          </VBtn>
          <VBtn
            v-if="ability.can('delete', 'Product')"
            icon size="small" variant="text" color="error"
            @click="confirmDelete(item)"
          >
            <VIcon icon="ri-delete-bin-line" />
            <VTooltip activator="parent">Hapus</VTooltip>
          </VBtn>
        </template>

        <!-- Empty state -->
        <template #no-data>
          <div class="text-center pa-8">
            <VIcon icon="ri-price-tag-3-line" size="64" color="grey-lighten-1" />
            <p class="text-body-1 mt-3 mb-1">Belum ada kategori</p>
            <p class="text-body-2 text-medium-emphasis mb-4">
              Buat kategori untuk mengorganisir produk Anda
            </p>
            <VBtn
              v-if="ability.can('create', 'Product')"
              color="primary" variant="tonal"
              @click="openCreate"
            >
              Tambah Kategori Pertama
            </VBtn>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <!-- ================================================================
         Dialog: Create / Edit
    ================================================================ -->
    <VDialog v-model="dialog" max-width="520" persistent>
      <VCard>
        <VCardTitle class="pa-4 pb-0 d-flex align-center justify-space-between">
          <span>{{ dialogMode === 'create' ? 'Tambah Kategori' : 'Edit Kategori' }}</span>
          <VBtn icon size="small" variant="text" @click="dialog = false">
            <VIcon icon="ri-close-line" />
          </VBtn>
        </VCardTitle>

        <VForm ref="formRef" @submit.prevent="submitForm">
          <VCardText class="pt-3">
            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model="form.name"
                  label="Nama Kategori"
                  placeholder="Minuman Panas"
                  :rules="[requiredValidator]"
                  :error-messages="errors.name"
                  autofocus
                />
              </VCol>
              <VCol cols="12" md="7">
                <VTextField
                  v-model="form.icon"
                  label="Icon (Remix Icon)"
                  placeholder="ri-cup-line"
                  prepend-inner-icon="ri-emotion-line"
                  :error-messages="errors.icon"
                  hint="Opsional"
                  persistent-hint
                />
              </VCol>
              <VCol cols="12" md="5">
                <VTextField
                  v-model.number="form.sort_order"
                  label="Urutan Tampil"
                  type="number"
                  min="0"
                  :error-messages="errors.sort_order"
                />
              </VCol>

              <!-- Color picker -->
              <VCol cols="12">
                <p class="text-body-2 font-weight-medium mb-2">Warna Kategori</p>
                <div class="d-flex align-center gap-2 flex-wrap">
                  <div
                    v-for="color in colorPresets"
                    :key="color"
                    class="color-swatch rounded cursor-pointer"
                    :style="{
                      width: '28px',
                      height: '28px',
                      backgroundColor: color,
                      border: form.color === color
                        ? '3px solid rgba(0,0,0,0.35)'
                        : '2px solid transparent',
                      outline: form.color === color ? '1px solid white' : 'none',
                      outlineOffset: '-4px',
                    }"
                    @click="form.color = color"
                  />
                  <VTextField
                    v-model="form.color"
                    density="compact"
                    hide-details
                    style="max-width: 120px"
                    placeholder="#1976D2"
                  >
                    <template #prepend-inner>
                      <div
                        class="rounded"
                        :style="{
                          width: '16px',
                          height: '16px',
                          backgroundColor: form.color,
                          border: '1px solid rgba(0,0,0,0.2)',
                        }"
                      />
                    </template>
                  </VTextField>
                </div>
              </VCol>

              <VCol cols="12">
                <VSwitch
                  v-model="form.is_active"
                  label="Kategori Aktif"
                  color="success"
                  hide-details
                />
              </VCol>
            </VRow>
          </VCardText>

          <VDivider />

          <VCardActions class="pa-4">
            <VSpacer />
            <VBtn variant="text" @click="dialog = false">Batal</VBtn>
            <VBtn type="submit" color="primary" :loading="dialogLoading">
              {{ dialogMode === 'create' ? 'Simpan' : 'Update' }}
            </VBtn>
          </VCardActions>
        </VForm>
      </VCard>
    </VDialog>

    <!-- ================================================================
         Dialog: Delete
    ================================================================ -->
    <VDialog v-model="deleteDialog" max-width="440">
      <VCard>
        <VCardTitle class="text-h6 pa-4 pb-0">Hapus Kategori?</VCardTitle>
        <VCardText class="pa-4 pt-2">
          Hapus kategori <strong>{{ selectedItem?.name }}</strong>?
          Kategori yang masih digunakan produk atau bahan baku tidak bisa dihapus.
        </VCardText>
        <VCardActions class="pa-4 pt-0">
          <VSpacer />
          <VBtn variant="text" @click="deleteDialog = false">Batal</VBtn>
          <VBtn color="error" :loading="loading" @click="deleteCategory">Hapus</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.color-swatch {
  transition: transform 0.1s;
  cursor: pointer;
}
.color-swatch:hover {
  transform: scale(1.18);
}
</style>
